import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, Pressable } from 'react-native';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import { Clock } from 'lucide-react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { ARENA_EMPTY_MONKEY_SOURCE } from '../../../constants/assets';
import type { CompetitionSummary } from '../../../src/competitions/types/competitions.types';
import { countdownParts } from '../../../src/competitions/helpers/competitionSections.helper';
import { CompetitionCrest } from '../CompetitionCrest/CompetitionCrest';
import {
  HUB_HERO_EYEBROW,
  HUB_HERO_EYEBROW_LIVE,
  HUB_HERO_CTA_UPCOMING,
  HUB_HERO_CTA_LIVE,
  HUB_COUNTDOWN_LABELS,
  HUB_COUNTDOWN_CAPTION,
  HUB_COUNTDOWN_TICK_MS,
  HUB_STATUS_LIVE_LABEL,
  HUB_STATUS_UPCOMING_ACCESSIBILITY,
  HUB_STATUS_CLOCK_ICON_SIZE,
  HUB_HERO_CREST_SIZE,
  HUB_HERO_CREST_LOGO_SIZE,
  HUB_TEST_IDS,
} from './CompetitionsHub.constants';
import { getCompetitionHeroStyles } from './CompetitionsHub.styles';

interface CompetitionHeroCardProps {
  competition: CompetitionSummary;
  onPress: (() => void) | null;
}

/**
 * The hub's future-first hero (specs/competitions-hub-client): the next thing to
 * play — LIVE competition, or the soonest UPCOMING one with a kickoff countdown.
 */
export const CompetitionHeroCard: React.FC<CompetitionHeroCardProps> = ({ competition, onPress }) => {
  const theme = useTheme();
  const s = useMemo(() => getCompetitionHeroStyles(theme), [theme]);

  const isLive = competition.lifecycle === 'LIVE';
  const displayName = competition.brand?.name ?? competition.name;

  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (isLive) return undefined;
    const id = setInterval(() => setNowMs(Date.now()), HUB_COUNTDOWN_TICK_MS);
    return () => clearInterval(id);
  }, [isLive]);
  const countdown = isLive ? null : countdownParts(competition.startDate, nowMs);

  // Same 3-stop brand gradient the squad hero banner uses.
  const gradientColors: [string, string, string] = [theme.primaryDark, theme.primary, theme.primaryLight];

  return (
    <View style={s.hero} testID={HUB_TEST_IDS.hero}>
      <LinearGradient
        colors={gradientColors}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={s.gradientFill}
      />
      <Image source={ARENA_EMPTY_MONKEY_SOURCE} style={s.mascot} contentFit="contain" />
      <View style={s.heroInner}>
        <View style={s.heroTopRow}>
          <CompetitionCrest
            logoUrl={competition.logoUrl}
            name={displayName}
            shortName={competition.brand?.shortName}
            flagCode={competition.brand?.flagCode}
            size={HUB_HERO_CREST_SIZE}
            logoSize={HUB_HERO_CREST_LOGO_SIZE}
            style={s.heroLogoCircle}
          />
          <View style={s.heroTitleBlock}>
            <Text style={s.heroEyebrow}>{isLive ? HUB_HERO_EYEBROW_LIVE : HUB_HERO_EYEBROW}</Text>
            <Text style={s.heroName} numberOfLines={2}>{displayName}</Text>
          </View>
        </View>

        {competition.description != null && competition.description !== '' && (
          <Text style={s.heroDescription} numberOfLines={2}>{competition.description}</Text>
        )}

        {countdown != null && (
          <View style={s.countdownStrip}>
            <View style={s.countdownBlock}>
              <Text style={s.countdownValue}>{countdown.days}</Text>
              <Text style={s.countdownLabel}>{HUB_COUNTDOWN_LABELS.days}</Text>
            </View>
            <View style={s.countdownBlock}>
              <Text style={s.countdownValue}>{countdown.hours}</Text>
              <Text style={s.countdownLabel}>{HUB_COUNTDOWN_LABELS.hours}</Text>
            </View>
            <View style={s.countdownBlock}>
              <Text style={s.countdownValue}>{countdown.minutes}</Text>
              <Text style={s.countdownLabel}>{HUB_COUNTDOWN_LABELS.minutes}</Text>
            </View>
            <Text style={s.countdownCaption}>{HUB_COUNTDOWN_CAPTION}</Text>
          </View>
        )}
        {/*
          The state, never a date (Story 16). A LIVE hero says so; an UPCOMING one whose
          kickoff has already passed — a stale lifecycle, or a start date the operator got
          wrong — shows a clock rather than announcing a date that has been and gone.
        */}
        {(isLive || countdown == null) && (
          <View
            style={s.heroStatusPill}
            testID={HUB_TEST_IDS.heroStatus}
            accessibilityRole={isLive ? undefined : 'image'}
            accessibilityLabel={isLive ? undefined : HUB_STATUS_UPCOMING_ACCESSIBILITY}
          >
            {isLive ? (
              <>
                <View style={s.heroLiveDot} />
                <Text style={s.heroStatusLabel}>{HUB_STATUS_LIVE_LABEL}</Text>
              </>
            ) : (
              <Clock size={HUB_STATUS_CLOCK_ICON_SIZE} color={theme.white} />
            )}
          </View>
        )}

        {onPress != null && (
          <Pressable
            style={({ pressed }) => [s.ctaButton, pressed && s.ctaButtonPressed]}
            onPress={onPress}
            testID={HUB_TEST_IDS.heroCta}
            accessibilityRole="button"
            accessibilityLabel={isLive ? HUB_HERO_CTA_LIVE : HUB_HERO_CTA_UPCOMING}
          >
            <Text style={s.ctaLabel}>{isLive ? HUB_HERO_CTA_LIVE : HUB_HERO_CTA_UPCOMING}</Text>
          </Pressable>
        )}
      </View>
    </View>
  );
};
